import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Music, Users, TrendingUp, Euro, Plus, Upload, Loader2 } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [form, setForm] = useState({ title: "", genre: "", duration: "", cover_url: "", audio_url: "", description: "" });
  const [uploading, setUploading] = useState({ audio: false, cover: false });
  const audioRef = useRef(null);
  const coverRef = useRef(null);

  const load = async () => {
    try {
      const [s, u, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/tracks"),
      ]);
      setStats(s.data); setUsers(u.data); setTracks(t.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const uploadFile = async (file, kind) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${data.file_url}`;
      setForm((f) => {
        const next = { ...f, [kind === "audio" ? "audio_url" : "cover_url"]: fullUrl };
        if (kind === "audio" && data.duration && !f.duration) next.duration = data.duration;
        return next;
      });
      const durMsg = kind === "audio" && data.duration ? ` · duration ${data.duration}` : "";
      toast.success(`${kind === "audio" ? "Audio" : "Cover"} uploaded (${Math.round(data.size / 1024)}KB)${durMsg}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const createTrack = async (e) => {
    e.preventDefault();
    if (!form.audio_url || !form.cover_url) {
      toast.error("Upload audio and cover first");
      return;
    }
    try {
      await api.post("/tracks", form);
      toast.success("Track added");
      setForm({ title: "", genre: "", duration: "", cover_url: "", audio_url: "", description: "" });
      if (audioRef.current) audioRef.current.value = "";
      if (coverRef.current) coverRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTrack = async (id) => {
    if (!confirm("Delete this track?")) return;
    await api.delete(`/tracks/${id}`);
    load();
  };

  return (
    <div className="container-x py-16">
      <div className="eyebrow mb-4">// Admin Console</div>
      <h1 className="section-heading mb-10">2B FUNK Panel</h1>

      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat icon={<Users className="w-5 h-5" />} label="Users" value={stats.users} testid="stat-users" />
          <Stat icon={<Music className="w-5 h-5" />} label="Tracks" value={stats.tracks} testid="stat-tracks" />
          <Stat icon={<TrendingUp className="w-5 h-5" />} label="Transactions" value={stats.paid_transactions} testid="stat-tx" />
          <Stat icon={<Euro className="w-5 h-5" />} label="Revenue" value={`€${stats.revenue_eur.toFixed(2)}`} testid="stat-revenue" />
        </div>
      )}

      {/* Upload Track */}
      <section className="mb-16">
        <h2 className="font-display font-bold text-2xl uppercase mb-4">Add Track</h2>
        <form onSubmit={createTrack} className="grid md:grid-cols-2 gap-4 bg-[#121217] border border-white/10 p-6">
          <Input label="Title" val={form.title} onChange={(v) => setForm({...form, title: v})} testid="admin-title" />
          <Input label="Genre" val={form.genre} onChange={(v) => setForm({...form, genre: v})} testid="admin-genre" />
          <Input label="Duration (mm:ss)" val={form.duration} onChange={(v) => setForm({...form, duration: v})} testid="admin-duration" />
          <Input label="Description" val={form.description} onChange={(v) => setForm({...form, description: v})} testid="admin-desc" />

          {/* Cover uploader */}
          <div className="md:col-span-1">
            <label className="block text-[10px] font-mono-plex uppercase tracking-widest text-white/50 mb-2">Cover</label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => coverRef.current?.click()} disabled={uploading.cover}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-white/10 bg-black hover:border-[#B3FF00]/40 text-sm font-mono-plex uppercase tracking-widest text-white/70"
                data-testid="admin-upload-cover-btn">
                {uploading.cover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading.cover ? "Uploading..." : (form.cover_url ? "Replace" : "Upload image")}
              </button>
              <input ref={coverRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "cover")}
                data-testid="admin-upload-cover-input" />
            </div>
            {form.cover_url && <img src={form.cover_url} alt="" className="mt-2 w-20 h-20 object-cover border border-white/10" />}
          </div>

          {/* Audio uploader */}
          <div className="md:col-span-1">
            <label className="block text-[10px] font-mono-plex uppercase tracking-widest text-white/50 mb-2">Audio File (MP3/WAV)</label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => audioRef.current?.click()} disabled={uploading.audio}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-white/10 bg-black hover:border-[#FF5E00]/40 text-sm font-mono-plex uppercase tracking-widest text-white/70"
                data-testid="admin-upload-audio-btn">
                {uploading.audio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading.audio ? "Uploading..." : (form.audio_url ? "Replace" : "Upload audio")}
              </button>
              <input ref={audioRef} type="file" accept="audio/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "audio")}
                data-testid="admin-upload-audio-input" />
            </div>
            {form.audio_url && (
              <div className="mt-2 text-[10px] font-mono-plex text-[#B3FF00] truncate" data-testid="admin-audio-url-preview">
                ✓ {form.audio_url.split("/").pop()}
              </div>
            )}
          </div>

          <button className="btn-primary flex items-center gap-2 md:col-span-2 justify-center" data-testid="admin-create-track-btn">
            <Plus className="w-4 h-4" /> Add to Catalog
          </button>
        </form>
      </section>

      {/* Tracks list */}
      <section className="mb-16">
        <h2 className="font-display font-bold text-2xl uppercase mb-4">Catalog ({tracks.length})</h2>
        <div className="border border-white/10 bg-[#121217] divide-y divide-white/5">
          {tracks.map((t) => (
            <div key={t.id} className="flex items-center gap-4 p-4">
              <img src={t.cover_url} alt="" className="w-12 h-12 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{t.title}</div>
                <div className="text-xs font-mono-plex text-white/40">{t.genre} · {t.duration}</div>
              </div>
              <button onClick={() => deleteTrack(t.id)} className="text-white/30 hover:text-red-400" data-testid={`admin-delete-${t.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Users */}
      <section>
        <h2 className="font-display font-bold text-2xl uppercase mb-4">Users ({users.length})</h2>
        <div className="border border-white/10 bg-[#121217] divide-y divide-white/5">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{u.name}</div>
                <div className="text-xs font-mono-plex text-white/40">{u.email} · {u.role}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-white/40">Credits</div>
                <div className="font-display font-bold text-[#B3FF00]">{u.credits ?? 0}</div>
              </div>
              <div className="text-right hidden sm:block min-w-[100px]">
                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-white/40">Plan</div>
                <div className="text-xs">{u.subscription_plan || "-"}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <div className="h-32" />
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="border border-white/10 bg-[#121217] p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-white/40 mb-2">{icon}<span className="text-xs font-mono-plex uppercase tracking-widest">{label}</span></div>
      <div className="font-display font-black text-3xl text-[#FF5E00]">{value}</div>
    </div>
  );
}

function Input({ label, val, onChange, testid, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] font-mono-plex uppercase tracking-widest text-white/50 mb-2">{label}</label>
      <input value={val} onChange={(e) => onChange(e.target.value)} required
        className="w-full bg-black border border-white/10 px-4 py-3 focus:outline-none focus:border-[#FF5E00] text-sm"
        data-testid={testid} />
    </div>
  );
}
